import React from 'react';

import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  AlternateEmail as AlternateEmailIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  MailOutline as MailIcon,
  ManageAccounts as ManageAccountsIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { Box, IconButton, ListItemIcon, ListItemText, MenuItem, MenuList } from '@mui/material';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';
import type { Nullable } from '@clearverify/types';
import { buildUrl } from '@clearverify/utils';

import {
  type EmployeeDataInfoFragment,
  EmployeeInviteIdDocument,
  UserStatus,
  useCreateEmployeeInviteLinkByCvStaffMutation,
  useGetEmployeeInviteLinkScopeForAdminQuery,
} from 'src/api';
import { Popover } from 'src/components/common';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import {
  type EmployeeInviteLinkArgs,
  type ResendInviteScope,
  useEmployeeInvite,
} from 'src/hooks/employee';
import { useModalV2 } from 'src/hooks/shared';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { MfaManagementModal } from 'src/modals/auth';
import { ConsumerManageAccountActivityModal } from 'src/modals/consumer';
import { DeleteEmployeeUserConfirmationModal } from 'src/modals/employee/DeleteEmployeeUserConfirmationModal';
import { EmployeeChangeEmailModal } from 'src/modals/employee/EmployeeChangeEmailModal';
import { SendEmployeeInviteModal } from 'src/modals/employee/SendEmployeeInviteModal';
import { GetWorkLetterModal } from 'src/modals/position';

type EmployeeDataSummaryActionsMenuProps = {
  entry: EmployeeDataInfoFragment;
  enableAdminControls?: boolean;
  inviteLinkArgs?: Nullable<EmployeeInviteLinkArgs>;
  employeeSsnEncrypted?: string;
  inviteEmailOptionsScope?: 'admin' | 'employerScope';
  sendInviteScope?: ResendInviteScope;
  onResendInvite?: (employeeId: string, inviteLinkArgs?: EmployeeInviteLinkArgs | null) => void;
  onInviteSent?: () => void;
};

export const EmployeeDataSummaryActionsMenu: React.FC<EmployeeDataSummaryActionsMenuProps> = ({
  entry,
  enableAdminControls = false,
  inviteLinkArgs,
  employeeSsnEncrypted,
  inviteEmailOptionsScope,
  sendInviteScope,
  onResendInvite,
  onInviteSent,
}) => {
  const menuButtonAriaId = React.useId();

  const { spawnNotification } = useNotification();
  const { openModal } = useModalV2();
  const { hasPlatformRoles, isPlatformAdmin } = useCurrentUserPermissions();

  const userId = entry.user?.id ?? null;
  const isPending = entry.user?.status === UserStatus.Pending;
  const { inviteId, loading: inviteLoading } = useEmployeeInvite(userId, { skip: !isPending });
  const [createInviteLink] = useCreateEmployeeInviteLinkByCvStaffMutation({
    refetchQueries: [EmployeeInviteIdDocument],
  });

  const employeeId = entry.id;
  const hasRegisteredUser = Boolean(entry.user && !isPending);

  // Employer/PEO/PEOPP callers pass an explicit scope; the admin flow has no
  // single employer context, so it's resolved server-side from the employee's
  // records. Only fetched when there are invite actions to build a link for.
  const shouldResolveScope =
    !inviteLinkArgs?.entityId &&
    inviteEmailOptionsScope === 'admin' &&
    !hasRegisteredUser &&
    Boolean(employeeSsnEncrypted);
  const { data: scopeData, loading: scopeLoading } = useGetEmployeeInviteLinkScopeForAdminQuery({
    variables: { employeeSsnEncrypted: employeeSsnEncrypted ?? '' },
    skip: !shouldResolveScope,
  });
  const selectedInviteLinkArgs: Nullable<EmployeeInviteLinkArgs> = inviteLinkArgs?.entityId
    ? inviteLinkArgs
    : (scopeData?.getEmployeeInviteLinkScopeForAdmin ?? null);
  const canUseAdminActions = enableAdminControls && hasPlatformRoles;
  // Unlike the other admin actions this includes Pending: the invite still has to reach the right
  // address, and signup takes its login from `User.email`.
  const canChangeEmail = canUseAdminActions && isPlatformAdmin && Boolean(entry.user);
  const canGenerateWorkLetter = canUseAdminActions && Boolean(employeeSsnEncrypted);
  const canCopyGenericInviteLink = !entry.user && Boolean(selectedInviteLinkArgs?.entityId);
  // A Pending employee can be left without an invite; platform staff get one created on copy.
  const canCreateInviteLink = isPending && !inviteId && canUseAdminActions;
  const canCopyInviteLink = Boolean(inviteId) || canCopyGenericInviteLink || canCreateInviteLink;
  const canSendInviteLink =
    !isPending &&
    !entry.user &&
    Boolean(sendInviteScope && inviteEmailOptionsScope && employeeSsnEncrypted && employeeId);
  // The resend recreates a missing invite, so it doesn't wait for one.
  const canResendInvite = isPending && Boolean(onResendInvite);
  const hasActions =
    canGenerateWorkLetter ||
    canUseAdminActions ||
    (!hasRegisteredUser && (canCopyInviteLink || canSendInviteLink || canResendInvite));

  const handleCopyInviteLink = React.useCallback(() => {
    if (canCreateInviteLink && employeeId) {
      // Safari drops the click's user activation across an await, so the clipboard write starts
      // now and waits for the link through the ClipboardItem promise.
      const link = createInviteLink({ variables: { employeeId } }).then(({ data }) => {
        const url = data?.createEmployeeInviteLinkByCVStaff?.inviteLinkUrl;
        if (!url) throw new Error('No invite link returned');
        return new Blob([url], { type: 'text/plain' });
      });
      navigator.clipboard
        .write([new ClipboardItem({ 'text/plain': link })])
        .then(() => spawnNotification('Invite link copied to clipboard', 'success'))
        // A failed mutation is already reported by the Apollo error handler; only a clipboard
        // failure after a link came back needs its own message.
        .catch(() =>
          link.then(
            () => spawnNotification('Failed to copy invite link', 'error'),
            () => {},
          ),
        );
      return;
    }

    const inviteLink = inviteId
      ? buildUrl(window.location.origin, appRoutes.auth.signup, {
          [RouterEntitiesId.invite]: inviteId,
        })
      : selectedInviteLinkArgs?.entityId
        ? buildUrl(window.location.origin, selectedInviteLinkArgs.route, {
            [selectedInviteLinkArgs.paramKey]: selectedInviteLinkArgs.entityId,
          })
        : null;

    if (!inviteLink) return;

    navigator.clipboard
      .writeText(inviteLink)
      .then(() => {
        if (inviteId) {
          spawnNotification('Invite link copied to clipboard', 'success');
          return;
        }

        const source = selectedInviteLinkArgs?.entityDisplayName
          ? ` for ${selectedInviteLinkArgs.entityDisplayName}`
          : '';
        spawnNotification(`Signup link${source} copied to clipboard`, 'success');
      })
      .catch(() =>
        spawnNotification(
          inviteId ? 'Failed to copy invite link' : 'Failed to copy signup link',
          'error',
        ),
      );
  }, [
    canCreateInviteLink,
    createInviteLink,
    employeeId,
    inviteId,
    selectedInviteLinkArgs,
    spawnNotification,
  ]);

  const handleSendInviteLink = React.useCallback(() => {
    if (!employeeId || !employeeSsnEncrypted || !sendInviteScope || !inviteEmailOptionsScope) {
      return;
    }

    openModal(SendEmployeeInviteModal, {
      id: ModalId.SendEmployeeInvite,
      employeeId,
      employeeSsnEncrypted,
      inviteEmailOptionsScope,
      sendInviteScope,
      inviteLinkArgs: selectedInviteLinkArgs,
      onInviteSent,
    });
  }, [
    employeeId,
    employeeSsnEncrypted,
    inviteEmailOptionsScope,
    openModal,
    selectedInviteLinkArgs,
    sendInviteScope,
    onInviteSent,
  ]);

  const handleResendInvite = React.useCallback(() => {
    if (!employeeId || !onResendInvite) return;
    onResendInvite(employeeId, selectedInviteLinkArgs);
  }, [employeeId, onResendInvite, selectedInviteLinkArgs]);

  const handleGenerateWorkLetter = React.useCallback(() => {
    if (!employeeSsnEncrypted) return;
    openModal(GetWorkLetterModal, {
      id: ModalId.GetWorkLetter,
      employeeSsnEncrypted,
      onGenerated: onInviteSent,
    });
  }, [employeeSsnEncrypted, openModal, onInviteSent]);

  const handleDisableMfa = React.useCallback(() => {
    if (!entry.user?.id) return;
    openModal(MfaManagementModal, { id: ModalId.MfaManagement, userId: entry.user.id });
  }, [entry.user?.id, openModal]);

  const handleChangeEmail = React.useCallback(() => {
    if (!entry.user) return;
    openModal(EmployeeChangeEmailModal, {
      id: ModalId.EmployeeChangeEmail,
      userId: entry.user.id,
      currentEmail: entry.user.email,
      onUpdated: onInviteSent,
    });
  }, [entry.user, openModal, onInviteSent]);

  const handleDeleteUser = React.useCallback(() => {
    if (!employeeId) return;
    openModal(DeleteEmployeeUserConfirmationModal, {
      id: ModalId.DeleteEmployeeUserConfirmationModal,
      employeeId,
      onDeleted: onInviteSent,
    });
  }, [employeeId, openModal, onInviteSent]);

  const handleManageAccountActivity = React.useCallback(() => {
    if (!employeeId) return;
    openModal(ConsumerManageAccountActivityModal, {
      id: ModalId.ConsumerManageAccountActivityModal,
      consumerId: employeeId,
      onUpdated: onInviteSent,
    });
  }, [employeeId, openModal, onInviteSent]);

  const renderMenu = React.useCallback(
    ({ onClose }: { onClose: () => void }) => (
      <MenuList aria-labelledby={menuButtonAriaId}>
        {canGenerateWorkLetter ? (
          <MenuItem
            onClick={withClose(handleGenerateWorkLetter, onClose)}
            disabled={Boolean(entry.dataDeletedAt)}
          >
            <ListItemIcon>
              <DescriptionIcon />
            </ListItemIcon>
            <ListItemText>Generate work letter</ListItemText>
          </MenuItem>
        ) : null}
        {!hasRegisteredUser && canCopyInviteLink ? (
          <MenuItem onClick={withClose(handleCopyInviteLink, onClose)}>
            <ListItemIcon>
              <ContentCopyIcon />
            </ListItemIcon>
            <ListItemText>{isPending ? 'Copy invite link' : 'Copy signup link'}</ListItemText>
          </MenuItem>
        ) : null}
        {canSendInviteLink ? (
          <MenuItem onClick={withClose(handleSendInviteLink, onClose)}>
            <ListItemIcon>
              <MailIcon />
            </ListItemIcon>
            <ListItemText>Invite</ListItemText>
          </MenuItem>
        ) : null}
        {canResendInvite ? (
          <MenuItem onClick={withClose(handleResendInvite, onClose)} disabled={!employeeId}>
            <ListItemIcon>
              <MailIcon />
            </ListItemIcon>
            <ListItemText>Resend invite</ListItemText>
          </MenuItem>
        ) : null}
        {canUseAdminActions && hasRegisteredUser ? (
          <MenuItem onClick={withClose(handleDisableMfa, onClose)}>
            <ListItemIcon>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <ListItemText>Manage MFA</ListItemText>
          </MenuItem>
        ) : null}
        {canChangeEmail ? (
          <MenuItem onClick={withClose(handleChangeEmail, onClose)}>
            <ListItemIcon>
              <AlternateEmailIcon />
            </ListItemIcon>
            <ListItemText>Change email</ListItemText>
          </MenuItem>
        ) : null}
        {canUseAdminActions && isPlatformAdmin && hasRegisteredUser ? (
          <MenuItem
            onClick={withClose(handleManageAccountActivity, onClose)}
            disabled={!employeeId}
          >
            <ListItemIcon>
              <ManageAccountsIcon />
            </ListItemIcon>
            <ListItemText>Manage Account Activity</ListItemText>
          </MenuItem>
        ) : null}
        {canUseAdminActions && isPlatformAdmin && entry.user ? (
          <MenuItem
            onClick={withClose(handleDeleteUser, onClose)}
            disabled={!employeeId}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText>Delete User</ListItemText>
          </MenuItem>
        ) : null}
      </MenuList>
    ),
    [
      menuButtonAriaId,
      entry.dataDeletedAt,
      entry.user,
      employeeId,
      isPending,
      hasRegisteredUser,
      isPlatformAdmin,
      canGenerateWorkLetter,
      canCopyInviteLink,
      canSendInviteLink,
      canResendInvite,
      canUseAdminActions,
      canChangeEmail,
      handleGenerateWorkLetter,
      handleCopyInviteLink,
      handleSendInviteLink,
      handleResendInvite,
      handleDisableMfa,
      handleChangeEmail,
      handleDeleteUser,
      handleManageAccountActivity,
    ],
  );

  if (!hasActions) return null;

  return (
    <Box>
      <Popover
        target={
          <IconButton
            id={menuButtonAriaId}
            aria-label="Employee actions"
            aria-haspopup="true"
            loading={inviteLoading || scopeLoading}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
        }
        content={renderMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </Box>
  );
};

const withClose = (action: () => void, onClose: () => void) => () => {
  action();
  onClose();
};
